"use client";

import React from "react";
import { Maximize2, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
const projects = [{
  id: 1,
  service: "Retaining Walls",
  location: "Squirrel Hill, PA",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/retaining_wall_after_pittsburgh.png",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/retaining_wall_before_pittsburgh.png",
  ratio: 4 / 3,
  description: "Complete structural overhaul and aesthetic upgrade of a failing hillside retaining wall."
}, {
  id: 2,
  service: "Patios",
  location: "Mt. Lebanon, PA",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/stone_patio_pittsburgh.png",
  ratio: 1,
  description: "Custom natural stone patio installation designed for outdoor family gatherings."
}, {
  id: 3,
  service: "Landscape Design",
  location: "Fox Chapel, PA",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/front_yard_design_after.png",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/front_yard_design_before.png",
  ratio: 16 / 9,
  description: "Full front yard redesign incorporating native plants and improved curb appeal."
}, {
  id: 4,
  service: "Tree and Shrub Care",
  location: "Upper St. Clair, PA",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/pruned_trees_garden.png",
  ratio: 3 / 4,
  description: "Expert pruning and shaping of mature ornamental trees to promote healthy growth."
}, {
  id: 5,
  service: "Spring Cleanups",
  location: "Monroeville, PA",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/spring_cleanup_mulch.png",
  ratio: 4 / 3,
  description: "Comprehensive seasonal cleanup including fresh premium mulching and bed edging."
}, {
  id: 6,
  service: "Lawn Maintenance",
  location: "Plum, PA",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586632/img/perfect_lawn_stripes.png",
  ratio: 16 / 9,
  description: "Regular professional lawn care resulting in a lush, healthy, and perfectly striped yard."
}];
export default function LocalProjectShowcase() {
  return <section id="local-project-showcase" className="py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <Badge variant="outline" className="border-primary/20 text-primary bg-primary/5">
            Our Portfolio
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight">
            Pittsburgh Projects Showcase
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl">See the difference experience makes. Take a look at some of our recent landscaping projects across Pittsburgh and surrounding communities.</p>
        </div>

        {}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
          {projects.map((project, index) => <div key={project.id} data-index={index} className="break-inside-avoid">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="w-full h-auto p-0 block rounded-xl overflow-hidden border-0 hover:bg-transparent focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background group relative cursor-zoom-in">
                    <AspectRatio ratio={project.ratio}>
                      <img src={project.imageAfter} alt={`${project.service} project in ${project.location}`} className="object-cover w-full h-full transition-transform duration-700 ease-in-out group-hover:scale-105" />
                      
                      {}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-3">
                        <Badge variant="secondary" className="pointer-events-none shadow-lg text-sm py-1 px-3">
                          {project.service}
                        </Badge>
                        <div className="flex items-center gap-2 text-white/90 text-sm font-medium bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-sm">
                          <Maximize2 className="size-4" />
                          <span>View Project</span>
                        </div>
                      </div>

                      {}
                      {project.imageBefore && <div className="absolute top-4 left-4 opacity-100 group-hover:opacity-0 transition-opacity duration-300">
                          <Badge variant="default" className="bg-primary/90 hover:bg-primary/90 shadow-sm">
                            Before & After
                          </Badge>
                        </div>}
                    </AspectRatio>
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden bg-background border-border shadow-2xl rounded-2xl">
                  <div className="flex flex-col max-h-[90vh]">
                    <DialogHeader className="p-6 pb-4 border-b border-border/50 bg-muted/30">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <DialogTitle className="text-2xl font-serif text-foreground">
                            {project.service}
                          </DialogTitle>
                          <DialogDescription className="text-base mt-1">
                            {project.location}
                          </DialogDescription>
                        </div>
                        <Badge variant="outline" className="hidden sm:inline-flex">
                          (Your Business Name) Landscaping
                        </Badge>
                      </div>
                    </DialogHeader>

                    <div className="overflow-y-auto p-6">
                      {project.imageBefore ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-muted-foreground uppercase tracking-wider text-sm">Before</h4>
                            </div>
                            <div className="rounded-xl overflow-hidden border border-border/50 shadow-sm">
                              <AspectRatio ratio={4 / 3}>
                                <img src={project.imageBefore} alt={`Before ${project.service}`} className="object-cover w-full h-full grayscale-[20%]" />
                              </AspectRatio>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-primary uppercase tracking-wider text-sm flex items-center gap-2">
                                After <ArrowRight className="size-4" />
                              </h4>
                            </div>
                            <div className="rounded-xl overflow-hidden border border-border shadow-md ring-1 ring-primary/10">
                              <AspectRatio ratio={4 / 3}>
                                <img src={project.imageAfter} alt={`After ${project.service}`} className="object-cover w-full h-full" />
                              </AspectRatio>
                            </div>
                          </div>
                        </div> : <div className="rounded-xl overflow-hidden border border-border shadow-md">
                          <img src={project.imageAfter} alt={project.service} className="w-full h-auto max-h-[60vh] object-contain bg-muted/20" />
                        </div>}

                      <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border/50">
                        <p className="text-foreground leading-relaxed">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}